import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import SVGIcon from '~/components/ui/Icons/SVGIcon';
import { CoreOfficeDataType } from '~/types/offices';
import { useLanguageParam } from '~/config/i18n';
import { luxonFormatOptions } from '~/components/utils/date';

type TProps = {
  office: CoreOfficeDataType;
  extraClasses?: string;
};

const OfficeAvailabilityCard = ({ office, extraClasses = '' }: TProps) => {
  const { isBookable, getDateText, getCardClasses, getDurationText, getIconType } = useOfficeAvailability(office);

  return (
    <div className={`${getCardClasses()} ${extraClasses}`}>
      <div className="d-flex align-items-start gap-2">
        <div className="availability-badge">
          <SVGIcon
            iconType={getIconType()}
            width="18"
            height="18"
          />
        </div>
        <div>
          <div className="text-2 -medium ">{getDateText()}</div>
          <div
            className={`text-2 -regular color-grey-500 ${isBookable ? '' : 'text-decoration-line-through'}`}
            dangerouslySetInnerHTML={{
              __html: getDurationText(),
            }}
          />
        </div>
      </div>
    </div>
  );
};

const useOfficeAvailability = (office: CoreOfficeDataType) => {
  const { t } = useTranslation();
  const lang = useLanguageParam();
  const now = DateTime.now().startOf('day');
  const startDate = DateTime.fromISO(office.earliestStartDate).startOf('day');
  const possibleStartDate = startDate > now ? startDate : now;
  const endDate = office.lastAvailabilityDate && DateTime.fromISO(office.lastAvailabilityDate).endOf('day');

  const isBookable = useMemo(
    () =>
      (office.publicationState === 'published' || office.publicationState === 'published_off_market') &&
      (!endDate || endDate.diff(possibleStartDate, 'months').months >= office.minDuration) &&
      (!endDate || endDate > now),
    [office, endDate, now, possibleStartDate],
  );

  const getDateText = () => {
    if (!isBookable) return t('office.show.availabilityCard.unavailable');
    const dateFormat = luxonFormatOptions.dayAndShortMonthWithYear[lang];
    const formattedStartDate = startDate.setLocale(lang).toFormat(dateFormat);
    const formattedEndDate = endDate && endDate.setLocale(lang).toFormat(dateFormat);
    if (startDate <= now) {
      return endDate
        ? t('office.show.availabilityCard.fromTodayToEndDate', { endDate: formattedEndDate })
        : t('office.show.availabilityCard.fromToday');
    }
    return endDate
      ? t('office.show.availabilityCard.fromExactDateToEndDate', {
          startDate: formattedStartDate,
          endDate: formattedEndDate,
        })
      : t('office.show.availabilityCard.fromExactDate', { date: formattedStartDate });
  };

  const getCardClasses = () => {
    if (!isBookable) return 'office-availability-card -unavailable';
    return startDate <= now ? 'office-availability-card -available' : 'office-availability-card -available-soon';
  };

  const getDurationText = () => {
    return office.minDuration === office.maxDuration
      ? t('office.show.availabilityCard.forMonths', {
          duration: office.minDuration,
          monthPluralization: t('global.pluralization.i18next.month', { count: office.minDuration }),
        })
      : t('office.show.availabilityCard.fromToMonths', {
          minDuration: office.minDuration,
          maxDuration: office.maxDuration,
        });
  };

  const getIconType = () => {
    return !isBookable || startDate <= now ? 'waitlist' : 'check-circle';
  };

  return {
    isBookable,
    getDateText,
    getCardClasses,
    getDurationText,
    getIconType,
  };
};

export default OfficeAvailabilityCard;
